import {
  AssignmentTurnedInOutlined as CaregiverIcon,
  FamilyRestroomRounded as FamilyIcon,
  QueryStatsRounded as SpecialistIcon,
} from '@mui/icons-material';
import { USER_ROLES } from '../../constants/roles';

export const ROLE_PERMISSION_GUIDE_ITEMS = [
  {
    role: USER_ROLES.CARE_PARTNER,
    route: '/invite/carepartner',
    roleKey: 'carePartner',
    title: 'Care Partner',
    badge: 'Shared Access',
    icon: FamilyIcon,
    description: 'Trusted family member or close support person who helps coordinate care.',
    can: [
      'View the full timeline',
      'Add and edit logs',
      'Edit the care profile and care details',
      'Add, edit, pause, or remove medication definitions',
      'Help coordinate routines and daily care',
      'Invite or manage other care members',
      'Log medication doses',
    ],
    cannot: [
      'Delete the child profile',
      'Transfer ownership',
      'Remove or replace the Care Owner',
    ],
    note: 'Care Partners can manage day-to-day care. Ownership stays with the Care Owner.',
    bestFor: ['Spouses or partners', 'Adult children', 'Siblings', 'Trusted relatives', 'Close friends'],
    chooseThisIf: 'This person helps manage care together with the family.',
    learnMore: 'Best for trusted family support that helps coordinate care with nearly the same day-to-day access as the Care Owner.',
  },
  {
    role: USER_ROLES.CAREGIVER,
    route: '/invite/caregiver',
    roleKey: 'caregiver',
    title: 'Caregiver',
    badge: 'Limited Access',
    icon: CaregiverIcon,
    description: 'Day-to-day helper with focused caregiving access.',
    can: [
      'Add daily updates and notes',
      'Track routines, meals, sleep, and activities',
      'View assigned schedules and routines',
      'Log medication doses when permitted',
    ],
    cannot: [
      'Manage the care team',
      'Change account settings',
      'Add or edit medication definitions',
      'Access unrelated private family information',
    ],
    bestFor: ['Home aides', 'Nannies', 'School staff', 'Sitters', 'Respite workers'],
    chooseThisIf: 'This person helps provide care or supervision, but does not need full family access.',
    learnMore: 'Best for helpers who need to keep the timeline updated without changing family settings.',
  },
  {
    role: USER_ROLES.THERAPIST,
    route: '/invite/therapist',
    roleKey: 'therapist',
    title: 'Specialist',
    badge: 'Professional Access',
    icon: SpecialistIcon,
    description: 'Professional support focused on progress, observations, and guidance.',
    can: [
      'Review shared logs and summaries',
      'Monitor patterns and progress',
      'Leave observations or recommendations',
    ],
    cannot: [
      'Manage family settings',
      'Change medication setup',
      'Invite care members',
      'Access unrelated private information',
    ],
    bestFor: ['Therapists', 'Clinicians', 'Support specialists', 'Consultants', 'Care coordinators'],
    chooseThisIf: 'This person mainly needs visibility into progress and care history.',
    learnMore: 'Best for professional guidance where context matters, but family management stays with the care circle.',
  },
];

export const HOSPICE_ROLE_PERMISSION_GUIDE_ITEMS = ROLE_PERMISSION_GUIDE_ITEMS.map((item) => {
  if (item.role === USER_ROLES.CARE_PARTNER) {
    return {
      ...item,
      description: 'Trusted family member or close support person who helps coordinate hospice care.',
      bestFor: ['Spouse or partner', 'Adult child', 'Sibling', 'Backup decision-maker', 'Trusted family coordinator'],
      chooseThisIf: 'This person helps coordinate family decisions, updates, and hospice communication.',
      learnMore: 'Best for trusted family support that helps manage care coordination without changing ownership.',
    };
  }

  if (item.role === USER_ROLES.CAREGIVER) {
    return {
      ...item,
      description: 'Hands-on or practical helper who may add day-to-day comfort updates.',
      bestFor: ['Private aide', 'Respite worker', 'Volunteer companion', 'Non-clinical helper', 'Family member doing daily updates'],
      chooseThisIf: 'This person helps with practical care or companionship and may log updates.',
      learnMore: 'Best for helpers who support daily comfort but should not manage family settings or medication setup.',
    };
  }

  if (item.role === USER_ROLES.THERAPIST) {
    return {
      ...item,
      description: 'Hospice professional or center contact who may need shared context but should not manage the app.',
      bestFor: ['Hospice nurse', 'Hospice center contact', 'Social worker', 'Chaplain or spiritual care', 'Physician or medical director', 'Pharmacist', 'Counselor'],
      chooseThisIf: 'This person is a hospice professional or advisor who may need context but is not managing family app settings.',
      learnMore: 'Most hospice center staff may belong in Who to Call rather than being invited. Invite as Specialist only if shared context or occasional guidance belongs in Lifelog.',
    };
  }

  return item;
});

export const ROLE_GUIDE_COPY = {
  default: {
    intro: 'Each role has different access levels to help keep care safe, organized, and collaborative.',
    examplesTitle: 'Common Examples',
    examples: [
      ['Spouse, adult child, sibling, or close family friend', 'Care Partner'],
      ['Home aide, nanny, school staff, sitter, or respite worker', 'Caregiver'],
      ['Therapist, doctor, care coordinator, or consultant', 'Specialist'],
    ],
    footer: 'You can update roles and permissions later as your care team evolves.',
  },
  hospice: {
    intro: 'The roles stay the same. Choose based on what this person should do in Lifelog, not only their real-world hospice title.',
    examplesTitle: 'Hospice Examples',
    examples: [
      ['Health proxy, spouse, adult child, sibling, or backup decision-maker', 'Care Partner'],
      ['Private aide, respite worker, companion, or family member logging daily updates', 'Caregiver'],
      ['Hospice nurse, center contact, social worker, chaplain, physician, pharmacist, or counselor', 'Specialist'],
    ],
    footer: 'Most hospice center staff may belong in Who to Call rather than being invited into Lifelog.',
  },
};

export const getRoleGuideLensKey = (child = {}) => {
  const careFocus = child?.careContext?.careFocus || child?.conditions || child?.concerns || [];
  const primaryFocus = Array.isArray(careFocus) ? careFocus[0] : careFocus;
  const primaryCode = String(primaryFocus?.code || primaryFocus || "").toUpperCase();
  const primaryLabel = String(primaryFocus?.label || primaryFocus || "").toLowerCase();

  return primaryCode === "HOSPICE" || primaryLabel.includes("hospice")
    ? "hospice"
    : "default";
};

export const getRolePermissionGuideItems = (child = {}) => (
  getRoleGuideLensKey(child) === "hospice"
    ? HOSPICE_ROLE_PERMISSION_GUIDE_ITEMS
    : ROLE_PERMISSION_GUIDE_ITEMS
);

export const getRoleGuideCopy = (child = {}) => (
  ROLE_GUIDE_COPY[getRoleGuideLensKey(child)] || ROLE_GUIDE_COPY.default
);
